import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { NotLoggedInBadge } from '@/components/NotLoggedInBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AddPeopleButton } from '@/features/accounts/AddPeopleSheet';
import {
  useUsers,
  type UsersSortBy,
  type UsersSortDir,
} from '@/hooks/useUsers';
import type { UsersSearch } from '@/lib/route-search';
import { cn } from '@/lib/utils';
import type { UserListItem } from '@/types/users';

import type { SortingState } from '@tanstack/react-table';
import type { MultiValue } from 'react-select';

const PER_PAGE = 50;

const PLAN_FILTER_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'plus_monthly', label: 'Plus Monthly' },
  { value: 'plus_annual', label: 'Plus Annual' },
  { value: 'power_monthly', label: 'Power Monthly' },
  { value: 'power_annual', label: 'Power Annual' },
] as const;

const PROFESSION_FILTER_OPTIONS = [
  { value: 'Academic', label: 'Academic' },
  { value: 'Administrative', label: 'Administrative' },
  { value: 'Communications', label: 'Communications' },
  { value: 'Consultant', label: 'Consultant' },
  { value: 'Customer Success', label: 'Customer Success' },
  { value: 'Data & Analytics', label: 'Data & Analytics' },
  { value: 'Design', label: 'Design' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Executive / Founder', label: 'Executive / Founder' },
  { value: 'Finance & Accounting', label: 'Finance & Accounting' },
  { value: 'Freelancer', label: 'Freelancer' },
  { value: 'Government / Non‑Profit', label: 'Government / Non‑Profit' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'HR / People Ops', label: 'HR / People Ops' },
  { value: 'IT & Support', label: 'IT & Support' },
  { value: 'Legal & Compliance', label: 'Legal & Compliance' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Product Management', label: 'Product Management' },
  { value: 'Project Management', label: 'Project Management' },
  { value: 'Recruiting', label: 'Recruiting' },
  { value: 'Research', label: 'Research' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Student', label: 'Student' },
  { value: 'Supply Chain & Logistics', label: 'Supply Chain & Logistics' },
  { value: 'Others', label: 'Others' },
  { value: 'Not Inferable', label: 'Not Inferable' },
] as const;

const columnHelper = createColumnHelper<UserListItem>();
type AllowedUsersSortBy = UsersSearch['sortBy'];

const DEFAULT_SORT_DIRECTION: Record<UsersSortBy, UsersSortDir> = {
  signup_date: 'desc',
  email: 'asc',
  name: 'asc',
  plan: 'asc',
  cancel_at_period_end: 'desc',
  total_chats: 'desc',
  total_routines: 'desc',
  total_routine_templates: 'desc',
  total_journals: 'desc',
};

type PlanOption = (typeof PLAN_FILTER_OPTIONS)[number];
type ProfessionOption = (typeof PROFESSION_FILTER_OPTIONS)[number];

function formatPlanLabel(value: string | null) {
  if (!value) return 'Basic';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(dateString: string | null) {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SortableHeader({
  label,
  isActive,
  direction,
  onToggle,
}: {
  label: string;
  isActive: boolean;
  direction?: UsersSortDir;
  onToggle: () => void;
}) {
  const Icon =
    !isActive || !direction
      ? ArrowUpDown
      : direction === 'asc'
        ? ArrowUp
        : ArrowDown;

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1 text-left font-semibold text-muted-foreground hover:text-foreground"
    >
      <span>{label}</span>
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export default function UsersTable() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/users' });
  const page = searchParams.page;
  const search = searchParams.search;
  const planFilterValues = searchParams.plans;
  const professionFilterValues = searchParams.professions;
  const sortBy = searchParams.sortBy;
  const sortDir = searchParams.sortDir;
  const [searchInput, setSearchInput] = useState(search);

  const updateSearch = useCallback(
    (updates: Partial<typeof searchParams>) => {
      navigate({ to: '/users', search: { ...searchParams, ...updates } });
    },
    [navigate, searchParams]
  );

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const queryOptions = useMemo(
    () => ({
      page,
      limit: PER_PAGE,
      search: search || undefined,
      sortBy,
      sortDir,
      filters:
        planFilterValues.length || professionFilterValues.length
          ? {
              plans: planFilterValues.length ? planFilterValues : undefined,
              professions: professionFilterValues.length
                ? professionFilterValues
                : undefined,
            }
          : undefined,
    }),
    [page, search, sortBy, sortDir, planFilterValues, professionFilterValues]
  );

  const { data, isLoading, error, refetch, isRefetching } =
    useUsers(queryOptions);
  const totalPages = data?.total_count
    ? Math.ceil(data.total_count / PER_PAGE)
    : 0;

  const sortingState = useMemo<SortingState>(
    () => [
      {
        id: sortBy,
        desc: sortDir === 'desc',
      },
    ],
    [sortBy, sortDir]
  );

  const handleSortChange = useCallback(
    (columnId: AllowedUsersSortBy) => {
      updateSearch({
        sortBy: columnId,
        sortDir:
          sortBy === columnId
            ? sortDir === 'asc'
              ? 'desc'
              : 'asc'
            : DEFAULT_SORT_DIRECTION[columnId],
        page: 1,
      });
    },
    [updateSearch, sortBy, sortDir]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: () => (
          <SortableHeader
            label="Name"
            isActive={sortBy === 'name'}
            direction={sortBy === 'name' ? sortDir : undefined}
            onToggle={() => handleSortChange('name')}
          />
        ),
        cell: ({ row }) => (
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {row.original.name || 'No name'}
            </p>
            {!row.original.has_logged_in && <NotLoggedInBadge />}
            {row.original.is_active === false && (
              <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
                Inactive
              </span>
            )}
          </div>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('email', {
        id: 'email',
        header: () => (
          <SortableHeader
            label="Email"
            isActive={sortBy === 'email'}
            direction={sortBy === 'email' ? sortDir : undefined}
            onToggle={() => handleSortChange('email')}
          />
        ),
        cell: ({ getValue }) => (
          <p className="text-sm text-muted-foreground">{getValue()}</p>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('plan', {
        id: 'plan',
        header: () => (
          <SortableHeader
            label="Plan"
            isActive={sortBy === 'plan'}
            direction={sortBy === 'plan' ? sortDir : undefined}
            onToggle={() => handleSortChange('plan')}
          />
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.plan ? 'default' : 'outline'}>
            {formatPlanLabel(row.original.plan)}
          </Badge>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('profession', {
        id: 'profession',
        header: () => (
          <span className="font-semibold text-muted-foreground">
            Predicted Profession
          </span>
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{getValue() || 'N/A'}</span>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('total_chats', {
        id: 'total_chats',
        header: () => (
          <SortableHeader
            label="Chats"
            isActive={sortBy === 'total_chats'}
            direction={sortBy === 'total_chats' ? sortDir : undefined}
            onToggle={() => handleSortChange('total_chats')}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{getValue() ?? 0}</span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('total_routine_templates', {
        id: 'total_routine_templates',
        header: () => (
          <SortableHeader
            label="Routine Templates"
            isActive={sortBy === 'total_routine_templates'}
            direction={
              sortBy === 'total_routine_templates' ? sortDir : undefined
            }
            onToggle={() => handleSortChange('total_routine_templates')}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{getValue() ?? 0}</span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('total_journals', {
        id: 'total_journals',
        header: () => (
          <SortableHeader
            label="Journals"
            isActive={sortBy === 'total_journals'}
            direction={sortBy === 'total_journals' ? sortDir : undefined}
            onToggle={() => handleSortChange('total_journals')}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{getValue() ?? 0}</span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('signup_date', {
        id: 'signup_date',
        header: () => (
          <SortableHeader
            label="Signup Date"
            isActive={sortBy === 'signup_date'}
            direction={sortBy === 'signup_date' ? sortDir : undefined}
            onToggle={() => handleSortChange('signup_date')}
          />
        ),
        cell: ({ getValue }) => (
          <span className="text-sm">{formatDate(getValue())}</span>
        ),
        enableSorting: true,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => (
          <span className="font-semibold text-muted-foreground">Actions</span>
        ),
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate({
                  to: '/users/$userId',
                  params: { userId: row.original.id.toString() },
                  search: { returnTo: searchParams },
                })
              }
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        ),
      }),
    ],
    [navigate, sortBy, sortDir, handleSortChange, searchParams]
  );

  const table = useReactTable({
    data: data?.users ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableMultiSort: false,
    state: { sorting: sortingState },
  });

  const hasFiltersApplied = Boolean(
    search || planFilterValues.length || professionFilterValues.length
  );

  const selectedPlanOptions = useMemo<PlanOption[]>(
    () =>
      PLAN_FILTER_OPTIONS.filter((option) =>
        planFilterValues.includes(option.value)
      ),
    [planFilterValues]
  );

  const selectedProfessionOptions = useMemo<ProfessionOption[]>(
    () =>
      PROFESSION_FILTER_OPTIONS.filter((option) =>
        professionFilterValues.includes(option.value)
      ),
    [professionFilterValues]
  );

  const handlePlanChange = (options: MultiValue<PlanOption>) => {
    updateSearch({ plans: options.map((option) => option.value), page: 1 });
  };

  const handleProfessionChange = (options: MultiValue<ProfessionOption>) => {
    updateSearch({
      professions: options.map((option) => option.value),
      page: 1,
    });
  };

  const accountsBar = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <AddPeopleButton />
    </div>
  );

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">
            Error Loading Users
          </CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {accountsBar}
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <CardTitle>Users</CardTitle>
              <CardDescription>
                View and manage all LittleBird app users
                {data?.total_count ? ` (${data.total_count} total)` : ''}
              </CardDescription>
            </div>
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              disabled={isRefetching}
            >
              <RefreshCw
                className={cn('h-4 w-4 mr-2', isRefetching && 'animate-spin')}
              />
              Refresh
            </Button>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:items-end">
            <div className="flex flex-[2] flex-col gap-2">
              <label className="text-xs font-semibold uppercase text-muted-foreground">
                Search
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      updateSearch({ search: searchInput, page: 1 });
                    }
                  }}
                  className="w-full rounded-md border bg-background py-2 pl-9 pr-4 text-sm"
                />
              </div>
            </div>

            <MultiSelectFilter<PlanOption>
              label="Plan Filter"
              options={PLAN_FILTER_OPTIONS}
              value={selectedPlanOptions}
              onChange={handlePlanChange}
              placeholder="Filter by plan..."
              classNamePrefix="plan-select"
            />

            <MultiSelectFilter<ProfessionOption>
              label="Profession Filter"
              options={PROFESSION_FILTER_OPTIONS}
              value={selectedProfessionOptions}
              onChange={handleProfessionChange}
              placeholder="Filter by profession..."
              classNamePrefix="profession-select"
            />

            <div className="flex gap-2">
              <Button
                onClick={() => updateSearch({ search: searchInput, page: 1 })}
                variant="outline"
              >
                Search
              </Button>
              {(hasFiltersApplied || searchInput) && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearchInput('');
                    updateSearch({
                      plans: [],
                      professions: [],
                      search: '',
                      page: 1,
                    });
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {hasFiltersApplied && (
            <div className="flex flex-wrap gap-2 text-xs">
              {search && (
                <AppliedFilter
                  label={`Search: ${search}`}
                  onRemove={() => {
                    setSearchInput('');
                    updateSearch({ search: '', page: 1 });
                  }}
                />
              )}
              {planFilterValues.map((plan) => (
                <AppliedFilter
                  key={`plan-${plan}`}
                  label={`Plan: ${PLAN_FILTER_OPTIONS.find((p) => p.value === plan)?.label || plan}`}
                  onRemove={() => {
                    updateSearch({
                      plans: planFilterValues.filter((value) => value !== plan),
                      page: 1,
                    });
                  }}
                />
              ))}
              {professionFilterValues.map((profession) => (
                <AppliedFilter
                  key={`profession-${profession}`}
                  label={`Profession: ${PROFESSION_FILTER_OPTIONS.find((p) => p.value === profession)?.label || profession}`}
                  onRemove={() => {
                    updateSearch({
                      professions: professionFilterValues.filter(
                        (value) => value !== profession
                      ),
                      page: 1,
                    });
                  }}
                />
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className="whitespace-nowrap"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="py-8 text-center text-muted-foreground"
                        >
                          {hasFiltersApplied
                            ? 'No users match the current filters'
                            : 'No users found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        updateSearch({ page: Math.max(1, page - 1) })
                      }
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        updateSearch({ page: Math.min(totalPages, page + 1) })
                      }
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AppliedFilter({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-accent/40 px-3 py-1 text-muted-foreground">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 hover:bg-muted"
        aria-label={`Remove ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
